import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Globezing";
const SITE_URL = typeof window !== "undefined" ? window.location.origin : "https://www.globezing.com";
const DEFAULT_DESCRIPTION =
  "A slow-travel journal by Ashwini & Hemant Parulkar. Editorial dispatches from the cliff-top villages of Cinque Terre, Scottish whisky trails, and everywhere else the road has taken us.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/globezing-logo-512.png`;

/**
 * SEO component — per-page title, description, canonical, OG/Twitter, and
 * JSON-LD schema (Organization, WebSite, BreadcrumbList, TravelArticle).
 *
 * Props:
 *  - title, description, image, path, type
 *  - article: { title, date, section, keywords } → emits TravelArticle JSON-LD
 *  - breadcrumbs: [{ name, path }] → emits BreadcrumbList JSON-LD
 */
export default function SEO({
  title,
  description,
  image,
  path = "",
  type = "website",
  article,
  breadcrumbs,
}) {
  const pageTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Travel stories from the slow road`;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  const schemas = [];

  // Always include Organization & WebSite on every page — Google uses the
  // strongest one and ignores duplicates.
  schemas.push({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/globezing-logo-512.png`,
    founder: { "@type": "Person", name: "Ashwini Parulkar" },
    sameAs: [],
  });
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/destinations?search={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });

  if (article) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "TravelArticle",
      headline: article.title,
      description: desc,
      image: ogImage,
      author: { "@type": "Person", name: "Ashwini Parulkar" },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/globezing-logo-512.png` },
      },
      datePublished: article.date,
      dateModified: article.date,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      articleSection: article.section,
      keywords: article.keywords,
    });
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {article?.date && <meta property="article:published_time" content={article.date} />}
      {article?.section && <meta property="article:section" content={article.section} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
